/**
 * HealingArcs — browse page for the Awakening-tier healing arc library.
 *
 * Responsibilities:
 *   - Tier check: non-Awakening users see an upsell prompt (no content hidden)
 *   - Load arc progress for the signed-in user
 *   - Render arc cards: available arcs + "Coming Soon" cards for future ones
 *   - No-concurrent-arc rule surfaced here as an informational note
 *
 * Route: /healing-arcs (Awakening-protected)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import { ARCS } from '../data/arcs'
import type { Arc, ArcId, ArcStatus } from '../data/arcs'
import { localizeArc } from '../data/arcs.pl'
import { useTranslation } from 'react-i18next'

// ── Icons ─────────────────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M12.5 15l-5-5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M4 7h6M7.5 4.5L10 7l-2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="3" y="6.5" width="8" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4.5 6.5V4.5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

// ── DB types ──────────────────────────────────────────────────────────────────

interface ArcProgress {
  arc_id: ArcId
  status: ArcStatus
  current_session: number
  session_1_completed_at: string | null
  session_2_completed_at: string | null
  session_3_completed_at: string | null
}

// ── Arc card ──────────────────────────────────────────────────────────────────

function SessionDots({ total, completed }: { total: number; completed: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: i < completed ? 'currentColor' : 'rgba(0,0,0,0.15)',
          }}
        />
      ))}
    </div>
  )
}

interface ArcCardProps {
  arc: Arc
  progress: ArcProgress | null
  onSelect: () => void
}

function ArcCard({ arc, progress, onSelect }: ArcCardProps) {
  const { t } = useTranslation()
  const completedSessions = progress
    ? [progress.session_1_completed_at, progress.session_2_completed_at, progress.session_3_completed_at]
        .filter(Boolean).length
    : 0

  const isInProgress = progress?.status === 'in_progress'
  const isCompleted  = progress?.status === 'completed'

  const statusLabel = isCompleted
    ? t('arcs.complete')
    : isInProgress
      ? t('arcs.sessionOfThree', { n: progress.current_session })
      : t('arcs.begin')

  if (!arc.available) {
    return (
      <div
        className="w-full text-left rounded-2xl opacity-60"
        style={{
          background: arc.colorSoft,
          borderTop: `3px solid ${arc.color}`,
          border: `1.5px solid #E4D5EE`,
          borderTopWidth: 3,
        }}
      >
        <div className="px-5 py-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: arc.color }}>
                {t('arcs.healingArc')}
              </p>
              <p className="font-display text-xl leading-snug" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
                {arc.name}
              </p>
            </div>
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full mt-1 flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.6)', border: `1px solid ${arc.color}30` }}
            >
              <span className="text-xs font-medium" style={{ color: arc.color }}>{t('arcs.comingSoonCaps')}</span>
            </div>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{arc.tagline}</p>
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={onSelect}
      className="w-full text-left transition-all hover:opacity-90 active:scale-[0.99] rounded-2xl"
      style={{
        background: arc.colorSoft,
        borderTop: `3px solid ${arc.color}`,
        border: `1.5px solid #E4D5EE`,
        borderTopWidth: 3,
        cursor: 'pointer',
      }}
    >
      <div className="px-5 py-5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: arc.color }}>
              {t('arcs.healingArc')}
            </p>
            <p className="font-display text-xl leading-snug" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
              {arc.name}
            </p>
          </div>

          {/* Session dots badge */}
          {progress && (
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full mt-1 flex-shrink-0"
              style={{
                background: 'rgba(255,255,255,0.7)',
                border: `1px solid ${arc.color}30`,
                color: arc.color,
              }}
            >
              <SessionDots total={arc.sessionCount} completed={completedSessions} />
            </div>
          )}
        </div>

        {/* Tagline */}
        <p className="text-sm leading-relaxed mb-2" style={{ color: '#6B5F7A' }}>{arc.tagline}</p>

        {/* Description */}
        <p className="text-xs leading-relaxed mb-4" style={{ color: '#9B8FAA' }}>{arc.description}</p>

        {/* Attribution */}
        <p className="text-xs mb-4" style={{ color: '#9B8FAA', fontStyle: 'italic' }}>
          {arc.attribution}
        </p>

        {/* Session pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {arc.sessions.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs"
              style={{
                background: i < completedSessions
                  ? arc.color
                  : 'rgba(255,255,255,0.5)',
                color: i < completedSessions ? '#fff' : '#9B8FAA',
                border: `1px solid ${arc.color}30`,
              }}
            >
              {s.title}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-center justify-end gap-1">
          <span className="text-xs font-medium" style={{ color: arc.color }}>{statusLabel}</span>
          <ArrowIcon />
        </div>
      </div>
    </button>
  )
}

// ── Upsell nudge ─────────────────────────────────────────────────────────────

function AwakeningUpsell({ onUpgrade }: { onUpgrade: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      className="rounded-2xl px-5 py-5 mb-6"
      style={{
        background: 'rgba(155,114,192,0.06)',
        border: '1.5px solid rgba(155,114,192,0.2)',
      }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(155,114,192,0.12)' }}
        >
          <LockIcon />
        </div>
        <div>
          <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>
            {t('arcs.partOfAwakening')}
          </p>
          <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
            {t('arcs.partOfAwakeningBody')}
          </p>
        </div>
      </div>
      <button
        onClick={onUpgrade}
        className="w-full py-3 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
        style={{ background: '#3D1F62', color: '#fff' }}
      >
        {/* The arrow stays outside the catalogue string — it is punctuation, not copy. */}
        {t('arcs.exploreAwakening')} →
      </button>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface HealingArcsProps {
  user: User
}

export default function HealingArcs({ user }: HealingArcsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const [tier, setTier] = useState<string | null>(null)
  const [progressMap, setProgressMap] = useState<Map<ArcId, ArcProgress>>(new Map())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        // Tier + arc progress in parallel
        const [profileResult, progressResult] = await Promise.all([
          Promise.resolve(
            db().from('profiles').select('subscription_tier').eq('id', user.id).single()
          ),
          Promise.resolve(
            db()
              .from('healing_arc_progress')
              .select('arc_id, status, current_session, session_1_completed_at, session_2_completed_at, session_3_completed_at')
              .eq('user_id', user.id)
          ),
        ])

        if (cancelled) return

        setTier((profileResult.data as { subscription_tier: string } | null)?.subscription_tier ?? 'free')

        const rows = (progressResult.data ?? []) as ArcProgress[]
        const map = new Map<ArcId, ArcProgress>()
        // Keep most recent in_progress; fall back to completed; ignore abandoned
        for (const row of rows) {
          const existing = map.get(row.arc_id)
          if (!existing || row.status === 'in_progress') {
            map.set(row.arc_id, row)
          }
        }
        setProgressMap(map)
      } catch {
        // Non-fatal: arc library still renders, just without progress state
        if (!cancelled) setTier('free')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [user.id])

  const isAwakening = tier === 'premium'

  const handleArcSelect = (arc: Arc) => {
    if (!isAwakening) return
    navigate(`/healing-arc/${arc.id}`)
  }

  return (
    <div
      className="min-h-svh flex flex-col"
      style={{
        background: 'radial-gradient(ellipse at 60% 0%, rgba(155,114,192,0.07) 0%, transparent 50%), #FAF7F4',
      }}
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="hover:opacity-70 transition-opacity"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
          aria-label={t('arcs.back')}
        >
          <BackIcon />
        </button>
        <div>
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>
            {t('arcs.awakening')}
          </p>
          <h1 className="font-display text-lg text-[#1C1128] font-normal" style={{ fontStyle: 'italic' }}>
            {t('arcs.healingArcs')}
          </h1>
        </div>
      </header>

      {/* Intro copy */}
      <div className="px-6 pb-4 max-w-xl mx-auto w-full">
        <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
          {t('arcs.intro')}
        </p>
      </div>

      <main className="flex-1 px-6 pb-16 max-w-xl mx-auto w-full page-enter">
        {loading ? (
          <div className="flex justify-center pt-12">
            <div className="w-5 h-5 rounded-full border-2 border-[#9B72C0] border-t-transparent animate-spin" />
          </div>
        ) : (
          <>
            {!isAwakening && (
              <AwakeningUpsell onUpgrade={() => navigate('/upgrade/awakening')} />
            )}

            <div className="flex flex-col gap-4">
              {ARCS.map(rawArc => localizeArc(rawArc, i18n.language)).map(arc => (
                <ArcCard
                  key={arc.id}
                  arc={arc}
                  progress={progressMap.get(arc.id) ?? null}
                  onSelect={() => handleArcSelect(arc)}
                />
              ))}
            </div>

            {/* Therapist normalization footer */}
            <p
              className="text-xs text-center leading-relaxed mt-8 px-4"
              style={{ color: '#9B8FAA' }}
            >
              {t('arcs.disclaimer1')}
              {t('arcs.disclaimer2')}
            </p>
          </>
        )}
      </main>
    </div>
  )
}
